import { getLocalApiOrigin } from '../localApi';
import { getPref, setPref } from '../persistentPrefs';
import { getSoundSettings } from './soundSettings';
import { k } from '../../localization/t';

/**
 * Local-only ambient sound layer for the game board.
 * One track plays at a time, loops seamlessly, and is completely separate
 * from the music/SFX channels. Never synced to the other player. Purely
 * local atmosphere for this client.
 *
 * Volume uses a sqrt curve so the lower half of the slider feels louder:
 * slider 0.5 → gain 0.707, slider 1.0 → gain 1.0. That matches the intent
 * that "70% is the new 50%" compared to a linear mapping.
 *
 * The curve is then scaled by the master volume, the same as music and SFX.
 * It used to bypass master entirely, which made master a control over
 * everything *except* the loudest continuous layer. So the only way to tame
 * ambience was to pull master down, and that silenced every sound effect as
 * collateral. At master 0.11 with the ambience slider at 1.0 the gap was 21dB.
 */

// Underwater + cave are intentionally excluded. Those clips are owned by
// the subsurface region view (see subsurfaceAmbience.js) and shouldn't be
// pickable as a standalone ambience track in the music library.
export const AMBIENCE_TRACKS = [
  { id: 'forest',         label: k('Forest'),         file: 'snd-ambience-forest.mp3' },
  { id: 'pasture',        label: k('Pasture'),        file: 'snd-ambience-pasture.mp3' },
  { id: 'beach',          label: k('Beach'),          file: 'snd-ambience-beach.mp3' },
  { id: 'swamp',          label: k('Swamp'),          file: 'snd-ambience-swamp.mp3' },
  { id: 'dungeon',        label: k('Dungeon'),        file: 'snd-ambience-dungeon.mp3' },
  { id: 'snowy-mountain', label: k('Snowy Mountain'), file: 'snd-ambience-snowy-mountain.mp3' },
];

const STORAGE_KEY = 'valkenhall-ambience-settings';
const DEFAULT_TRACK_ID = 'forest';
const DEFAULT_VOLUME = 0.5;

let state = null;
let audio = null;
let isPlaying = false;
// While suppressed, `playAmbience()` is a no-op and the active audio
// fades out without flipping `state.playing`. The user's intent is
// preserved so a subsequent `setAmbienceSuppressed(false)` can resume
// the same track. Used by the subsurface region view so the cave +
// underwater pair takes over without overwriting the user's pick.
let suppressed = false;
let fadeTimer = null;
// Transient multiplier applied on top of the user's slider while a
// notification ducks the bed. Always returns to 1. Anything that seizes the
// gain knob mid-duck (suppression, stop) must reset it via `clearDuck()`, or
// the bed would come back permanently quiet.
let duckFactor = 1;
let duckTimer = null;

function clearDuck() {
  clearTimeout(duckTimer);
  duckTimer = null;
  duckFactor = 1;
}
const listeners = new Set();

function loadState() {
  if (state) return state;
  const parsed = getPref(STORAGE_KEY, null) || {};
  state = {
    trackId: AMBIENCE_TRACKS.some((t) => t.id === parsed.trackId) ? parsed.trackId : DEFAULT_TRACK_ID,
    volume: typeof parsed.volume === 'number' ? parsed.volume : DEFAULT_VOLUME,
    playing: parsed.playing === true,
  };
  return state;
}

function persist() {
  setPref(STORAGE_KEY, state);
}

function setPersistedPlaying(value) {
  const s = loadState();
  if (s.playing === value) return;
  s.playing = value;
  persist();
}

function notify() {
  for (const fn of listeners) fn();
}

function effectiveVolume() {
  const v = Math.max(0, Math.min(1, loadState().volume));
  return Math.sqrt(v) * getSoundSettings().masterVolume * duckFactor;
}

/**
 * Re-apply the gain after a master-volume change in Settings. Ambience owns
 * its own audio element, so unlike music it isn't covered by
 * updateMusicVolume() and needs its own poke.
 */
export function updateAmbienceVolume() {
  if (audio && !suppressed && !fadeTimer) audio.volume = effectiveVolume();
}

/**
 * Dip the bed briefly so a notification can land on top of it, then bring it
 * back. Ramps down fast and returns slowly. A duck that recovers as quickly
 * as it fell reads as a dropout, whereas a slow return just feels like the
 * room settling.
 *
 * A no-op while suppressed (the subsurface view owns the channel) or mid-fade,
 * because both of those are already driving `audio.volume` and a duck would
 * fight them for the knob.
 */
export function duckAmbience({ depth = 0.3, downMs = 180, holdMs = 900, upMs = 1600 } = {}) {
  if (!audio || suppressed || fadeTimer) return;
  clearTimeout(duckTimer);
  duckFactor = depth;
  fadeAudioTo(effectiveVolume, downMs, () => {
    duckTimer = setTimeout(() => {
      duckFactor = 1;
      fadeAudioTo(effectiveVolume, upMs);
    }, holdMs);
  });
}

function trackUrl(file) {
  return `${getLocalApiOrigin()}/game-assets/${file}`;
}

function getCurrentTrack() {
  const s = loadState();
  return AMBIENCE_TRACKS.find((t) => t.id === s.trackId) || AMBIENCE_TRACKS[0];
}

function destroyAudio() {
  if (audio) {
    try { audio.pause(); } catch {}
    audio.src = '';
    audio = null;
  }
}

function clearFadeTimer() {
  if (fadeTimer) {
    clearInterval(fadeTimer);
    fadeTimer = null;
  }
}

// Linear gain ramp on `audio.volume`. Cancels any ramp already in
// flight so rapid suppress/un-suppress toggles don't stack. `to` may
// be a getter: a ramp toward the user's level re-reads it every tick,
// so a settings change mid-fade lands on the new level.
function fadeAudioTo(to, durationMs, onDone) {
  if (!audio) { if (onDone) onDone(); return; }
  clearFadeTimer();
  const start = audio.volume;
  const steps = 30;
  const stepMs = Math.max(8, Math.floor(durationMs / steps));
  let i = 0;
  fadeTimer = setInterval(() => {
    i++;
    if (!audio) { clearFadeTimer(); if (onDone) onDone(); return; }
    const target = typeof to === 'function' ? to() : to;
    if (i >= steps) {
      audio.volume = target;
      clearFadeTimer();
      if (onDone) onDone();
      return;
    }
    audio.volume = start + (target - start) * (i / steps);
  }, stepMs);
}

export function getAmbienceState() {
  const s = loadState();
  return {
    trackId: s.trackId,
    volume: s.volume,
    isPlaying,
  };
}

export function subscribeAmbience(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function playAmbience() {
  if (suppressed) {
    // Preserve intent so resume on un-suppress works, but emit no
    // sound right now. The subsurface ambience owns the channel.
    isPlaying = true;
    setPersistedPlaying(true);
    notify();
    return;
  }
  const track = getCurrentTrack();
  if (!audio || audio.dataset.trackId !== track.id) {
    destroyAudio();
    audio = new Audio(trackUrl(track.file));
    audio.loop = true;
    audio.dataset.trackId = track.id;
  }
  clearFadeTimer();
  clearDuck();
  audio.volume = effectiveVolume();

  const playingAudio = audio;
  audio.play().catch(() => {
    // Autoplay blocked. Retry on next user gesture.
    const resume = () => {
      if (audio === playingAudio && audio.paused) {
        audio.play().catch(() => {});
      }
      document.removeEventListener('pointerdown', resume);
      document.removeEventListener('keydown', resume);
    };
    document.addEventListener('pointerdown', resume, { once: true });
    document.addEventListener('keydown', resume, { once: true });
  });

  isPlaying = true;
  setPersistedPlaying(true);
  notify();
}

export function pauseAmbience() {
  if (audio) {
    try { audio.pause(); } catch {}
  }
  isPlaying = false;
  setPersistedPlaying(false);
  notify();
}

export function toggleAmbience() {
  if (isPlaying) pauseAmbience();
  else playAmbience();
}

/** Teardown used when leaving the game board. */
export function stopAmbience({ silent = false } = {}) {
  clearFadeTimer();
  destroyAudio();
  isPlaying = false;
  suppressed = false;
  if (!silent) notify();
}

export function setAmbienceTrack(trackId) {
  const s = loadState();
  if (!AMBIENCE_TRACKS.some((t) => t.id === trackId) || s.trackId === trackId) return;
  s.trackId = trackId;
  persist();
  if (isPlaying) {
    destroyAudio();
    playAmbience();
  } else {
    notify();
  }
}

export function setAmbienceVolume(volume) {
  const s = loadState();
  s.volume = Math.max(0, Math.min(1, volume));
  persist();
  // Suppression owns the gain knob while active. Don't fight the
  // fade by writing the user-target volume on top of it.
  if (audio && !suppressed && !fadeTimer) audio.volume = effectiveVolume();
  notify();
}

/**
 * Fade-out / fade-in the user's ambience without changing
 * `state.playing`. The subsurface region view sets this to `true`
 * while active so the cave + underwater pair owns the ambience
 * channel; flipping back to `false` resumes the user's track from
 * silence to their slider level.
 *
 * Fade durations default to 1500ms (matches the subsurface
 * audio orchestrator) but can be overridden for instant teardown.
 */
export function setAmbienceSuppressed(value, { fadeMs = 1500 } = {}) {
  const next = Boolean(value);
  if (next === suppressed) return;
  suppressed = next;
  // Suppression takes the gain knob off the duck. Otherwise a duck that was
  // mid-hold when the subsurface view took over would restore to a ducked
  // level on the way back out.
  clearDuck();
  if (suppressed) {
    if (audio) {
      fadeAudioTo(0, fadeMs, () => {
        if (suppressed && audio) {
          try { audio.pause(); } catch {}
        }
      });
    }
    return;
  }
  // Un-suppressing: if the user wants ambience playing, bring it back.
  const s = loadState();
  if (!s.playing) return;
  const track = getCurrentTrack();
  if (!audio || audio.dataset.trackId !== track.id) {
    destroyAudio();
    audio = new Audio(trackUrl(track.file));
    audio.loop = true;
    audio.dataset.trackId = track.id;
  }
  audio.volume = 0;
  audio.play().catch(() => {});
  isPlaying = true;
  fadeAudioTo(effectiveVolume, fadeMs);
  notify();
}

/**
 * Auto-resume ambience on app start if it was playing at last shutdown.
 * Mirrors `hydrateCustomMusic` so both managers behave the same way
 * across restarts. Safe to call multiple times. `playAmbience` is
 * idempotent when audio is already running.
 */
export function hydrateAmbience() {
  if (loadState().playing) playAmbience();
}
